import { useQuery } from "@tanstack/react-query";
import { BarChart, Card, MIN_MODAL_CONTENT_HEIGHT } from "components";
import { useMemo, useState } from "react";
import {
  ChartExport,
  useExportableChart,
} from "../../../../../../../../shared/components";
import { Props } from "../../../../WebAnalytics.types";
import { getPersonaTopVisitor } from "../../Visualization.requests";

export const PersonaTopWebsiteVisitor = ({
  pixelId,
  accountView,
  campaignId,
  startDate,
  endDate,
  accountsIds,
  visitedPages,
  includeIspTraffic,
  includeKnownBotTraffic,
  includePotentialBotTraffic,
  personas,
}: Props) => {
  const { data: rawData, isFetching: isFetching } = useQuery({
    queryKey: [
      "webAnalyticsPersonaTopVisitors",
      pixelId,
      accountView,
      campaignId,
      startDate,
      endDate,
      accountsIds,
      visitedPages,
      includeIspTraffic,
      includeKnownBotTraffic,
      includePotentialBotTraffic,
      personas,
    ],
    queryFn: () =>
      getPersonaTopVisitor(pixelId, {
        accountView,
        campaignId,
        startDate,
        endDate,
        accountsIds,
        visitedPages,
        includeIspTraffic,
        includeKnownBotTraffic,
        includePotentialBotTraffic,
        personas,
      }),
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const data = useMemo(() => {
    if (isFetching || !rawData?.data) {
      return [];
    }

    return rawData.data.map(({ personaName, pageVisits }) => ({
      label: personaName,
      value: Number(pageVisits),
    }));
  }, [rawData, isFetching]);

  const chart = useExportableChart({
    title: `${String(data[0]?.label)} Persona is the Top Website Visitor by ${
      data[1]?.value !== 0
        ? (((data[0]?.value - data[1]?.value) / data[1]?.value) * 100).toFixed(
            2
          )
        : "N/A"
    }%`,
    campaignId: campaignId || undefined,
    chart: (
      <BarChart
        minHeight={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : undefined}
        stretch
        data={data}
        orientation="horizontal"
        showXAxis={true}
      />
    ),
    dates: {
      start: startDate,
      end: endDate,
    },
  });

  return (
    <Card
      onExpandedChange={setIsExpanded}
      isLoading={isFetching}
      title={chart.title}
      hasData={!isFetching && Boolean(data?.length)}
      isExpandable
      actions={
        <ChartExport
          size="small"
          campaignId={campaignId || undefined}
          title={chart.title}
          onExportPNG={chart.downloadAsPng}
          data={[
            {
              label: "Business Decision Makers",
              value: 120,
            },
            {
              label: "Technical Decision Makers",
              value: 80,
            },
            {
              label: "Other Personas",
              value: 50,
            },
          ]}
        />
      }
    >
      {chart.component}
      {chart.exportComponent}
    </Card>
  );
};
